import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectAllLanguages } from '../../store/languages/languages.selects';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Language } from '../../models/language.model';
import { filter } from 'rxjs/operators';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { ResponsiveService } from '../../services/reponsive.service';
import { LucideAngularModule } from 'lucide-angular';
import { MagicHoverDirective } from '../../directives/magic-hover.directive';
import { DownloadService } from '../../services/download.service';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpResponse,
} from '@angular/common/http';
import { LottieComponent } from 'ngx-lottie';
import { TranslateModule } from '@ngx-translate/core';

type SvetAudioElement = {
  language: Language;
  element: HTMLAudioElement;
  blob?: Observable<HttpEvent<Blob>>;
};

@Component({
  selector: 'app-demo-player',
  imports: [
    NgStyle,
    AsyncPipe,
    LucideAngularModule,
    NgClass,
    MagicHoverDirective,
    LottieComponent,
    TranslateModule,
  ],
  templateUrl: './demo-player.component.html',
  styleUrl: './demo-player.component.scss',
})
export class DemoPlayerComponent {
  private store: Store = inject(Store);
  private downloadService: DownloadService = inject(DownloadService);
  private responsiveService: ResponsiveService = inject(ResponsiveService);

  @ViewChild('videoPlayer', { static: false })
  videodRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('demoPlayer', { static: false })
  demoPlayerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('soundList', { static: false })
  soundListRef!: ElementRef<HTMLUListElement>;

  // 🤳 Responsive
  smallScreen$: Observable<boolean> = this.responsiveService.small$;
  mediumScreen$: Observable<boolean> = this.responsiveService.desktop$;
  largeScreen$: Observable<boolean> = this.responsiveService.largeDesktop$;

  private shortLanguages: string[] = ['fr', 'en', 'es', 'de', 'it']; // liste des langues à afficher

  subscriptions: Subscription[] = [];
  playerVolume: number = 0.5;

  selectedLanguage$: BehaviorSubject<Language | undefined> =
    new BehaviorSubject<Language | undefined>(undefined);
  isPlaying$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  languages: Language[] = [];
  audiosElements: SvetAudioElement[] = [];
  audioUrls: string[] = [];

  interval: any;
  intervalDuration: number = 5000; // 5 seconds
  soundsEnabled: boolean = true;

  play(): void {
    this.changeVideoState('play');
    this.isPlaying$.next(true);
  }

  pause(): void {
    this.changeVideoState('pause');
    this.isPlaying$.next(false);
  }

  changeVideoState(state: 'play' | 'pause'): void {
    if (!this.demoPlayerRef) return;
    Array.from(this.demoPlayerRef.nativeElement.children).forEach(
      (child: Element) => {
        if (child.tagName === 'AUDIO') {
          const audioElement = child as HTMLAudioElement;
          if (
            this.selectedLanguage$.getValue()?.short !==
            audioElement.id.split('-')[1]
          ) {
            audioElement.volume = 0;
          }
          state === 'play' ? audioElement.play() : audioElement.pause();
        }
        if (child.tagName === 'VIDEO') {
          const videoElement = child as HTMLVideoElement;
          state === 'play' ? videoElement.play() : videoElement.pause();
        }
      }
    );
  }

  toggleSounds() {
    this.soundsEnabled = !this.soundsEnabled;
  }

  onLanguageChange(language: Language): void {
    if (this.selectedLanguage$.getValue() === language) {
      this.selectedLanguage$.next(undefined);
      return;
    }
    this.selectedLanguage$.next(language);
  }

  initInterval(): void {
    if (this.selectedLanguage$.getValue() === undefined)
      this.selectedLanguage$.next(this.languages[0]);
    this.clearInterval();
    this.interval = setInterval(() => {
      const index = this.languages.findIndex(
        (lang: Language) => lang.id === this.selectedLanguage$.getValue()?.id
      );
      this.selectedLanguage$.next(
        this.languages[index + 1] || this.languages[0]
      );
    }, this.intervalDuration);
  }

  clearInterval(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  createAudioPlayer(lang: Language, blob: Observable<HttpEvent<Blob>>): void {
    const audioPlayer: HTMLAudioElement = new Audio();
    audioPlayer.classList.add('audioPlayer');
    audioPlayer.volume = 0;
    audioPlayer.setAttribute('step', '0.01');
    audioPlayer.id = `audio-${lang.short}`;
    const subscription = blob.subscribe({
      next: (event: HttpEvent<Blob>) => {
        if (event.type === HttpEventType.Response) {
          const response: HttpResponse<Blob> = event;
          if (response.body) {
            const audioUrl: string = URL.createObjectURL(response.body);
            this.audioUrls.push(audioUrl);
            audioPlayer.src = audioUrl;
            audioPlayer.load();
          }
        }
      },
      error: (error) => {
        console.error(
          `Erreur lors du chargement de l'audio ${lang.short}:`,
          error
        );
      },
    });
    this.subscriptions.push(subscription);

    if (this.demoPlayerRef?.nativeElement) {
      this.demoPlayerRef.nativeElement.appendChild(audioPlayer);
    }
    this.audiosElements.push({ language: lang, element: audioPlayer, blob });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.subscriptions.push(
        this.store
          .select(selectAllLanguages)
          .pipe(
            filter((languages: readonly Language[]) => languages.length > 0)
          )
          .subscribe({
            next: (languages: readonly Language[]) => {
              this.languages = [
                ...languages.filter((lang: Language) =>
                  this.shortLanguages.includes(lang.short)
                ),
              ];
              this.languages.forEach((lang: Language) => {
                const langAudioBlob$: Observable<HttpEvent<Blob>> =
                  this.downloadService.downloadAudioDemo(lang.short);
                this.createAudioPlayer(lang, langAudioBlob$);
                this.selectedLanguage$.next(
                  this.languages.find((lang: Language) => lang.id === 1)
                );
              });
              const videoBlob$: Observable<HttpEvent<Blob>> =
                this.downloadService.downloadVideoDemo();
              const subscription: Subscription = videoBlob$.subscribe({
                next: (event: HttpEvent<Blob>) => {
                  if (event.type === HttpEventType.Response) {
                    const response: HttpResponse<Blob> =
                      event as HttpResponse<Blob>;
                    if (response.body) {
                      const videoUrl: string = URL.createObjectURL(
                        response.body
                      );
                      this.subscriptions.push(subscription);
                      this.videodRef.nativeElement.src = videoUrl;
                      this.videodRef.nativeElement.load();

                      this.videodRef.nativeElement.addEventListener(
                        'canplay',
                        () => {
                          this.isLoading$.next(false);
                        }
                      );

                      this.videodRef.nativeElement.addEventListener(
                        'play',
                        () => {
                          this.isPlaying$.next(true);
                        }
                      );

                      this.videodRef.nativeElement.addEventListener(
                        'pause',
                        () => {
                          this.isPlaying$.next(false);
                        }
                      );

                      this.videodRef.nativeElement.addEventListener(
                        'ended',
                        () => {
                          this.isPlaying$.next(false);
                        }
                      );
                    }
                  }
                },
                error: (error: HttpErrorResponse) => {
                  console.error(
                    'Erreur lors du chargement de la vidéo:',
                    error
                  );
                },
              });
            },
            error: (error: HttpErrorResponse) => {
              console.error('Erreur lors du chargement des langues:', error);
            },
          })
      );
    }, 0);

    this.selectedLanguage$.subscribe((lang: Language | undefined) => {
      if (lang) {
        const selectAudioPlayer: HTMLAudioElement | null = <HTMLAudioElement>(
          document.getElementById(`audio-${lang.short}`)
        );
        if (selectAudioPlayer) {
          selectAudioPlayer.volume = this.playerVolume;
        }
        this.languages
          .filter((language: Language) => language.id !== lang.id)
          .forEach((language: Language) => {
            const audioPlayer: HTMLAudioElement | null = <HTMLAudioElement>(
              document.getElementById(`audio-${language.short}`)
            );
            if (audioPlayer) {
              audioPlayer.volume = 0;
            }
          });
      }
    });
    this.isPlaying$.subscribe((isPlaying: boolean) => {
      if (isPlaying) {
        this.initInterval();
      } else {
        this.clearInterval();
      }
    });
  }

  ngOnDestroy() {
    this.clearInterval();
    this.audioUrls.forEach((url: string) => URL.revokeObjectURL(url));
    if (this.videodRef?.nativeElement) this.videodRef.nativeElement.pause();
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}
